/**
 * Platform Account Service
 * Manages media platform account connections for the workspace
 */

import { getSupabaseClient } from "@/config/supabase";
import { toJson } from "./dbBoundary";
import type { DbPlatform } from "@/types/dbEnums";
import { isAbandoned } from "./mediaApi/reapDecision";
import { logError as logError } from "@/services/logger";
import type {
  PlatformAccount,
  PlatformType,
  ConnectionMethod,
  SyncStatus,
} from "@/types";
import type { TFn } from "@/utils/channelCorrelation";
import { CodedError } from "./codedError";
import { derivePassiveStatus } from "./platformAccountStatus";
import { ACTIVE_JOB_STATUSES, isAlreadyQueued } from "./syncJobShared";

/**
 * Frontend PlatformType <-> DB `platform` enum bridge.
 *
 * The UI names Google's products by API (`google_ads`, `google_analytics`), but the
 * DB enum + every sync client key them as `google` / `analytics`. Without this
 * translation, connecting Google would insert `platform: 'google_ads'` and violate
 * the enum, and a stored `google` row would never match the Google Ads card
 * (`account.platform === platform.type`). facebook/tiktok/line map identity.
 */
export const toDbPlatform = (p: PlatformType): DbPlatform =>
  p === "google_ads" ? "google" : p === "google_analytics" ? "analytics" : p;

export const fromDbPlatform = (p: string): PlatformType =>
  p === "google" ? "google_ads" : p === "analytics" ? "google_analytics" : (p as PlatformType);

/**
 * The columns a client may read from media_platform_accounts.
 *
 * `select('*')` is no longer usable here: migration 055 revokes SELECT on
 * access_token/refresh_token from `authenticated`, and Postgres fails the
 * WHOLE statement with "permission denied" rather than quietly omitting a
 * column the caller cannot see. Naming the columns is load-bearing, not style.
 *
 * has_access_token / has_refresh_token are generated booleans standing in for
 * the values: this list only ever needed to know whether a credential exists.
 *
 * last_verified_at has its OWN grant (migration 104), for the same reason in
 * reverse: with table-level SELECT revoked here since 055, a column nobody
 * named in a GRANT does not fail loudly — it reads back as `undefined`, which
 * is indistinguishable from "never verified". Adding the column to this list
 * without the grant would have shipped as a fix that fixed nothing.
 */
const ACCOUNT_COLUMNS =
  "id, workspace_id, platform, account_name, external_account_id, " +
  "has_access_token, has_refresh_token, token_expires_at, connection_method, " +
  "account_type, sync_error, last_synced_at, last_verified_at, created_at, updated_at";

/**
 * Database row for media_platform_accounts
 */
interface MediaPlatformAccountRow {
  id: string;
  workspace_id: string;
  platform: string;
  external_account_id: string;
  account_name: string;
  currency: string;
  timezone: string;
  // The tokens themselves are NOT client-readable (migration 055) — these
  // generated booleans are all a browser gets to know about them.
  has_access_token?: boolean;
  has_refresh_token?: boolean;
  token_expires_at?: string;
  /**
   * NOT a database column — an in-memory enrichment. api/_lib/enrichAccountWithSecret
   * merges the server-only secret config onto the account under this key before
   * handing it to a platform client. Writing it back would 400 (PGRST204).
   */
  platform_config?: Record<string, unknown>;
  connection_method?: string;
  last_synced_at?: string;
  /**
   * When the platform last accepted this credential in a live check. The
   * browser can read it and cannot write it (migration 104 holds no
   * INSERT/UPDATE grant for authenticated on this column) — it is the server's
   * word, not the client's claim about itself.
   */
  last_verified_at?: string;
  sync_error?: string;
  created_at: string;
  updated_at: string;
}

/**
 * Re-exported, not defined here: derivePassiveStatus moved to
 * ./platformAccountStatus when this file hit the 800-line ceiling. Kept on this
 * path because two test files and every future reader already look for it here,
 * and a status rule is what people come to the account service to find.
 */
export { derivePassiveStatus };

/**
 * Convert database row to PlatformAccount
 */
function toPlatformAccount(row: MediaPlatformAccountRow): PlatformAccount {
  const now = new Date();
  const expiresAt = row.token_expires_at ? new Date(row.token_expires_at) : null;

  const status = derivePassiveStatus({
    hasAccessToken: Boolean(row.has_access_token),
    connectionMethod: row.connection_method,
    tokenExpiresAt: row.token_expires_at,
    hasRefreshToken: Boolean(row.has_refresh_token),
    syncError: row.sync_error,
    lastSyncedAt: row.last_synced_at,
    lastVerifiedAt: row.last_verified_at,
    now,
  });

  // "valid" only where a token exists to be valid. Reporting a missing
  // credential as a valid one is how the account list came to disagree with
  // whether syncing could possibly work.
  // "missing" only where a token is the credential this connection uses —
  // a service account legitimately has none.
  const tokenIsTheCredential = row.connection_method !== "service_account";
  let tokenStatus: PlatformAccount["tokenStatus"] =
    row.has_access_token || !tokenIsTheCredential ? "valid" : "missing";
  if (row.has_access_token && expiresAt && expiresAt < now) {
    tokenStatus = "expired";
  }

  return {
    id: row.id,
    platform: fromDbPlatform(row.platform),
    accountName: row.account_name,
    accountId: row.external_account_id,
    status,
    lastSyncAt: row.last_synced_at,
    lastVerifiedAt: row.last_verified_at,
    createdAt: row.created_at,
    workspaceId: row.workspace_id,
    expiresAt: row.token_expires_at,
    tokenStatus,
    connectionMethod: (row.connection_method as ConnectionMethod | null) ?? undefined,
  };
}

/**
 * Get all connected platform accounts for a workspace
 */
export async function getConnectedAccounts(
  workspaceId: string
): Promise<PlatformAccount[]> {
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from("media_platform_accounts")
    .select(ACCOUNT_COLUMNS)
    .eq("workspace_id", workspaceId)
    .order("created_at", { ascending: false });

  if (error) {
    logError("PlatformAccount", "Error fetching platform accounts:", error);
    throw new Error(`Failed to fetch platform accounts: ${error.message}`);
  }

  // ACCOUNT_COLUMNS is not a literal, so supabase-js cannot infer the row
  // shape and widens to GenericStringError — cast through unknown.
  return ((data as unknown as MediaPlatformAccountRow[]) || []).map(toPlatformAccount);
}

/**
 * Get a single platform account by ID
 */
export async function getPlatformAccount(
  accountId: string
): Promise<PlatformAccount | null> {
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from("media_platform_accounts")
    .select(ACCOUNT_COLUMNS)
    .eq("id", accountId)
    .single();

  if (error) {
    if (error.code === "PGRST116") {
      return null;
    }
    logError("PlatformAccount", "Error fetching platform account:", error);
    throw new Error(`Failed to fetch platform account: ${error.message}`);
  }

  return toPlatformAccount(data as unknown as MediaPlatformAccountRow);
}


/**
 * Connect a new platform account
 */
export async function connectPlatform(
  workspaceId: string,
  platform: PlatformType,
  credentials: {
    access_token: string;
    refresh_token?: string;
    account_id: string;
    account_name?: string;
    expires_at?: string;
    method?: ConnectionMethod;
  }
): Promise<PlatformAccount> {
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from("media_platform_accounts")
    .insert({
      workspace_id: workspaceId,
      platform: toDbPlatform(platform),
      external_account_id: credentials.account_id,
      account_name: credentials.account_name || `${platform} Account`,
      currency: "THB",
      timezone: "Asia/Bangkok",
      access_token: credentials.access_token,
      refresh_token: credentials.refresh_token,
      token_expires_at: credentials.expires_at,
      // Default to 'token': the OAuth-redirect path calls onConnect without a
      // method, and that path only ever used the token/manual credentials form.
      connection_method: credentials.method ?? "token",
    })
    // ACCOUNT_COLUMNS, not a bare .select(): that means RETURNING *, which
    // includes the two token columns `authenticated` cannot read since
    // migration 055. Postgres fails the WHOLE statement rather than dropping a
    // column the caller may not see, so the write itself errored — connecting
    // a platform, and renaming one, both failed with 42501.
    .select(ACCOUNT_COLUMNS)
    .single();

  if (error) {
    // 23505 = unique_violation on uq_media_platform_accounts_ws_platform_extid:
    // this account is already connected to this workspace (DB-level guard, the
    // real backstop behind the UI's in-memory check).
    if (error.code === "23505") {
      throw new CodedError("accountAlreadyConnected", "This account is already connected to this workspace.");
    }
    logError("PlatformAccount", "Error connecting platform:", error);
    throw new Error(`Failed to connect platform: ${error.message}`);
  }

  return toPlatformAccount(data as unknown as MediaPlatformAccountRow);
}

/**
 * Save server-only secret config for a connection (Google service-account JSON,
 * developer token, etc.) into media_platform_account_secrets.
 *
 * That table has NO SELECT RLS policy — this write path can insert/delete but can
 * never read a private key back, and the browser-side sync path can't read it at
 * all (only the service_role sync worker can). See migration 20240101000029.
 *
 * Delete-then-insert, NOT upsert: PostgREST's upsert emits `INSERT ... ON CONFLICT
 * DO UPDATE`, which Postgres rejects under RLS when the table has no SELECT policy
 * (it can't read the conflicting row) — a plain INSERT is fine. Deleting first
 * makes reconnect (overwrite the existing secret) idempotent; a no-op when none
 * exists. No `.select()` on either call: returning=minimal needs no SELECT policy.
 */
export async function savePlatformSecret(
  accountId: string,
  workspaceId: string,
  config: Record<string, unknown>,
): Promise<void> {
  const supabase = getSupabaseClient();

  // Clear any existing secret for this account (reconnect); harmless if none.
  await supabase.from("media_platform_account_secrets").delete().eq("account_id", accountId);

  const { error } = await supabase
    .from("media_platform_account_secrets")
    .insert({ account_id: accountId, workspace_id: workspaceId, config: toJson(config) });

  if (error) {
    logError("PlatformAccount", "Error saving platform secret:", error);
    throw new Error(`Failed to save connection credentials: ${error.message}`);
  }
}

/**
 * Update an existing platform account
 */
export async function updatePlatformAccount(
  accountId: string,
  updates: Partial<{
    access_token: string;
    refresh_token: string;
    token_expires_at: string;
    account_name: string;
    external_account_id: string;
    // platform_config is NOT a column on media_platform_accounts. Measured on
    // local AND prod: the table has no such column, and a PATCH carrying it
    // answers 400 PGRST204 "Could not find the 'platform_config' column ... in
    // the schema cache". The per-account config lives in
    // media_platform_account_secrets.config (savePlatformSecret), and the
    // `platform_config` field on PlatformAccountRow above is an IN-MEMORY
    // enrichment that api/_lib/enrichAccountWithSecret merges server-side — not
    // a column either. It was offered here and never passed by any caller.
    connection_method: ConnectionMethod;
    last_synced_at: string;
    sync_error: string;
  }>
): Promise<PlatformAccount> {
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from("media_platform_accounts")
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq("id", accountId)
    // ACCOUNT_COLUMNS, not a bare .select(): that means RETURNING *, which
    // includes the two token columns `authenticated` cannot read since
    // migration 055. Postgres fails the WHOLE statement rather than dropping a
    // column the caller may not see, so the write itself errored — connecting
    // a platform, and renaming one, both failed with 42501.
    .select(ACCOUNT_COLUMNS)
    .single();

  if (error) {
    logError("PlatformAccount", "Error updating platform account:", error);
    // Unique index on (workspace_id, platform, external_account_id) — surface a
    // duplicate account id as an actionable message rather than a raw DB error.
    if (error.code === "23505") {
      throw new CodedError("accountIdAlreadyConnected", "An account with this ID is already connected on this platform.");
    }
    throw new Error(`Failed to update platform account: ${error.message}`);
  }

  return toPlatformAccount(data as unknown as MediaPlatformAccountRow);
}

/**
 * Disconnect (delete) a platform account
 */
export async function disconnectPlatform(accountId: string): Promise<void> {
  const supabase = getSupabaseClient();

  const { error } = await supabase
    .from("media_platform_accounts")
    .delete()
    .eq("id", accountId);

  if (error) {
    logError("PlatformAccount", "Error disconnecting platform:", error);
    throw new Error(`Failed to disconnect platform: ${error.message}`);
  }
}


/**
 * Trigger sync for a specific platform account
 */
export async function triggerSync(
  accountId: string,
  t: TFn,
  // Both or neither: the worker syncs a window only when it has both ends, and
  // migration 103 refuses a row that carries one (sync_jobs_window_has_both_ends).
  options?: {
    startDate: string;
    endDate: string;
  }
): Promise<{ success: boolean; message: string; syncId?: string }> {
  try {
    const account = await getPlatformAccount(accountId);
    if (!account) {
      return {
        success: false,
        message: t("settings.syncAccountNotFound"),
      };
    }

    // Enqueue a job; a background worker runs the sync (see the sync_jobs
    // migration). A full sync is ~48-120s of sequential API calls — far too slow
    // for the browser, and platforms like TikTok block browser CORS entirely.
    // sync_jobs.workspace_id is NOT NULL, while PlatformAccount.workspaceId is
    // optional. Same guard, same reason as queueBackfill: a 23502 naming the
    // column tells the user nothing about which account was misconfigured.
    if (!account.workspaceId) {
      return {
        success: false,
        message: t("settings.syncAccountNotFound"),
      };
    }

    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from("sync_jobs")
      .insert({
        workspace_id: account.workspaceId,
        account_id: accountId,
        // TRANSLATED, not copied. `account.platform` is the UI vocabulary
        // (`fromDbPlatform` put it there), and sync_jobs.platform is the
        // STORED one — migration 080 constrains it to exactly the five names
        // media_platform_accounts uses. Copying it straight through wrote
        // `google_ads` / `google_analytics`, which 080's CHECK rejects with a
        // 23514: the Sync button on Google Ads and on Google Analytics has
        // answered 400 ever since, on every click, while the other three
        // platforms map identity and kept working. 080's own header says "no
        // writer produces the old spelling any more" — this was one of two
        // that still did.
        platform: toDbPlatform(account.platform),
        status: "queued",
        start_date: options?.startDate ?? null,
        end_date: options?.endDate ?? null,
        source: "manual",
      })
      .select("id")
      .single();

    // Migration 103 allows one waiting routine job per account: a click while a
    // scheduled (or earlier) job is queued is refused with 23505. The sync the
    // user asked for is already on its way — before 103 this inserted a second
    // job and the account synced twice. Follow the waiting job; if even that
    // lookup fails, the job still exists, so it is still a success.
    if (isAlreadyQueued(error)) {
      const active = supabase
        .from("sync_jobs")
        .select("id")
        .eq("account_id", accountId)
        .in("status", [...ACTIVE_JOB_STATUSES]);
      // Asked by the key of the index that refused the insert, so the answer is
      // THAT job. "The newest waiting job" is a backfill month whenever history
      // was requested after the routine job was queued.
      const { data: waiting } = await (options
        ? active.eq("start_date", options.startDate).eq("end_date", options.endDate)
        : active.is("start_date", null)
      )
        .limit(1)
        .maybeSingle();
      const waitingId = (waiting as { id: string } | null)?.id;
      return waitingId
        ? { success: true, message: "Sync already queued", syncId: waitingId }
        : { success: true, message: "Sync already queued" };
    }

    if (error || !data) {
      logError("PlatformAccount", "Failed to enqueue sync", error);
      return { success: false, message: error?.message || t("settings.syncQueueFailed") };
    }

    return {
      success: true,
      message: "Sync queued",
      syncId: (data as { id: string }).id,
    };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error";
    logError("PlatformAccount", "Error triggering sync:", error);
    return {
      success: false,
      message: t("settings.syncStartFailed", { reason: errorMessage }),
    };
  }
}

/**
 * How long a job may sit unclaimed before the UI stops calling it progress.
 *
 * Generous on purpose: a worker busy with another account is a normal reason to
 * wait, and crying "broken" at the first slow minute trains people to ignore the
 * warning. Past this, no worker is running.
 */
const STALLED_AFTER_MS = 10 * 60 * 1000;

/**
 * How long a claimed job may run before the UI stops believing in it.
 *
 * Far longer than the queued threshold, and deliberately so: Facebook's adaptive
 * date-range halving can legitimately keep one job going for hours, and calling
 * that abandoned would train people to ignore the warning. Past this, the worker
 * that claimed it is gone — nothing ever revisits a 'running' row, because
 * claimNextJob only looks at 'queued'.
 */
/*
 * Imported, no longer re-declared. This was a second copy of the reaper's
 * constant living in a second file, and the two had to be changed together by
 * somebody who knew both existed — which is how a badge and the decision
 * behind it drift apart. `isAbandoned` now answers for both, so the account
 * owner reads the same verdict the reaper acts on.
 */

/**
 * Get sync status for a platform account
 */
export async function getSyncStatus(accountId: string, t: TFn): Promise<SyncStatus> {
  try {
    const account = await getPlatformAccount(accountId);
    if (!account) {
      return {
        isSyncing: false,
        lastSync: null,
        error: t("settings.syncAccountNotFound"),
      };
    }

    // Read the latest job from the durable queue (set by the background worker),
    // not the old 30s-window heuristic on last_synced_at.
    const supabase = getSupabaseClient();
    const { data: job } = await supabase
      .from("sync_jobs")
      .select("status, error_message, entities_processed, completed_at, created_at, started_at, heartbeat_at")
      .eq("account_id", accountId)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (!job) {
      return { isSyncing: false, lastSync: account.lastSyncAt || null, error: null };
    }

    const j = job as {
      status: string;
      error_message: string | null;
      entities_processed: number | null;
      completed_at: string | null;
      created_at: string | null;
      started_at: string | null;
      heartbeat_at: string | null;
    };

    // Queued and running were reported as one state, so a job nobody claimed
    // showed the same spinner as one making progress — and because nothing ever
    // moved it on, the spinner and the disabled button lasted forever. Clicking
    // "Sync all" with no worker running produced exactly that: five queued rows
    // with a null started_at, and a UI that said Syncing.
    const isQueued = j.status === "queued";
    const isRunning = j.status === "running";
    // Both ends of the same failure. A job nobody claimed and a job whose worker
    // died look identical from here — the row simply stops changing — and only
    // the first was being caught, so a worker that fell over mid-sync left the
    // account spinning for good.
    const waitedTooLong =
      isQueued && j.created_at != null && Date.now() - new Date(j.created_at).getTime() > STALLED_AFTER_MS;
    // A job that is still beating is running, however long it has been at it.
    // Before the heartbeat existed this read `started_at` alone, so a genuine
    // four-hour backfill told the account owner their sync had been abandoned.
    const ranTooLong =
      isRunning &&
      isAbandoned({ startedAt: j.started_at, heartbeatAt: j.heartbeat_at, now: new Date() });
    const isStalled = waitedTooLong || ranTooLong;

    return {
      isSyncing: isQueued || isRunning,
      isQueued,
      isStalled,
      stalledReason: ranTooLong ? "abandoned" : waitedTooLong ? "unclaimed" : undefined,
      queuedSince: isQueued ? j.created_at : null,
      lastSync: j.completed_at || account.lastSyncAt || null,
      error: j.status === "failed" ? j.error_message || t("settings.syncFailedShort") : null,
      entitiesProcessed: j.entities_processed ?? undefined,
    };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error";
    return {
      isSyncing: false,
      lastSync: null,
      error: errorMessage,
    };
  }
}


/**
 * Per-platform DATA frontier: the latest metric date each platform has
 * actually delivered, plus the earliest date on record (for the Workspace
 * tab's "ข้อมูลย้อนหลัง N วัน" tile).
 *
 * Settings shows this INSTEAD of leading with "synced X ago": a sync job can
 * finish green while the data stands still (Google sat at 08-24 through a
 * week of successful runs), so delivery — not job status — is what the
 * frontier reports. Keys are UI PlatformType (google_ads, google_analytics);
 * the DB enum translation stays inside this module via toDbPlatform.
 *
 * Reads media_ad_set_metrics because it is the adset-grain truth every
 * dashboard sums (includes PMax and GA analytics rows the ad grain misses).
 */
export interface PlatformFrontiers {
  /** PlatformType -> latest YYYY-MM-DD with data; absent = never delivered */
  frontiers: Map<PlatformType, string>;
  /**
   * Platforms whose lookup FAILED. Absence from `frontiers` alone cannot
   * distinguish "never delivered" from "we could not find out", and the UI
   * was reporting the second as the first.
   */
  unreadable: Set<PlatformType>;
  /** Earliest metric date across all platforms, null when there is no data */
  firstDate: string | null;
}

export async function fetchPlatformFrontiers(
  workspaceId: string,
  platformTypes: PlatformType[]
): Promise<PlatformFrontiers> {
  const supabase = getSupabaseClient();

  const [latestPerPlatform, earliest] = await Promise.all([
    Promise.all(
      platformTypes.map(async (type) => {
        const { data, error } = await supabase
          .from("media_ad_set_metrics")
          .select("date")
          .eq("workspace_id", workspaceId)
          .eq("platform", toDbPlatform(type))
          .order("date", { ascending: false })
          .limit(1);
        if (error) {
          logError("PlatformAccount", "Error fetching data frontier:", error);
          return [type, null, true] as const;
        }
        return [type, (data?.[0]?.date as string | undefined) ?? null, false] as const;
      })
    ),
    supabase
      .from("media_ad_set_metrics")
      .select("date")
      .eq("workspace_id", workspaceId)
      .order("date", { ascending: true })
      .limit(1),
  ]);

  const frontiers = new Map<PlatformType, string>();
  const unreadable = new Set<PlatformType>();
  for (const [type, date, failed] of latestPerPlatform) {
    if (failed) unreadable.add(type);
    else if (date) frontiers.set(type, date);
  }

  if (earliest.error) {
    logError("PlatformAccount", "Error fetching earliest metric date:", earliest.error);
  }

  return {
    frontiers,
    unreadable,
    firstDate: (earliest.data?.[0]?.date as string | undefined) ?? null,
  };
}

/**
 * Recent sync jobs for a workspace — the only signal that says "nothing is
 * running".
 *
 * The account row cannot carry it: a job nobody claims writes no error, moves
 * no timestamp and changes no status, so every passive field still reads
 * healthy while the queue fills up. Measured on this workspace: six jobs
 * queued and unclaimed while all five accounts reported "active".
 *
 * Bounded by time AND count so a long-idle workspace does not read its whole
 * history to answer one header pill.
 */
export async function getRecentSyncJobs(
  workspaceId: string,
  sinceHours = 48,
): Promise<Array<{ platform: string; status: string; errorMessage: string | null; createdAt: string }>> {
  const since = new Date(Date.now() - sinceHours * 3_600_000).toISOString();
  const { data, error } = await getSupabaseClient()
    .from("sync_jobs")
    .select("platform, status, error_message, created_at")
    .eq("workspace_id", workspaceId)
    .gte("created_at", since)
    .order("created_at", { ascending: false })
    .limit(50);

  if (error) {
    // Thrown, not swallowed: the caller renders "could not check" rather than a
    // green pill, and an empty array here would be indistinguishable from a
    // healthy queue.
    logError("PlatformAccount", "Error reading sync jobs:", error);
    throw new Error(error.message);
  }

  return (data ?? []).map((r) => ({
    platform: String(r.platform),
    status: String(r.status),
    errorMessage: (r.error_message as string | null) ?? null,
    createdAt: String(r.created_at),
  }));
}
